import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export type DebitExplanationKind = 'operator_transfer' | 'bank_fee' | 'other';

/** A bank debit that no payout, no return and no confirmed explanation accounts for. */
export interface UnexplainedDebit {
  bank_transaction_id: string;
  concentration_account_id: string;
  account_label: string | null;
  posted_at: string;
  amount: string;
  currency_code: string;
  /** The identifier the payment rail gave the transfer, as the bank reported it. */
  rail_trace: string | null;
  /** What the bank printed. Shown to help a person recognise the line; never matched on. */
  bank_text: string | null;
  /** The bank gave its own transaction id for the line: an `other` explanation may then go
   *  without an attached document. */
  has_provider_reference: boolean;
}

/** A confirmed funds transfer to a fund's operator the debit may be. Picked, never typed. */
export interface TransferCandidate {
  transfer_id: string;
  fund_id: string;
  reference_number: string;
  amount: string;
  currency_code: string;
  transferred_on: string;
  /** The debit's rail trace equals the transfer's reference. */
  trace_matches: boolean;
}

/** An explanation waiting for a manager who did not propose it. */
export interface DebitProposal {
  id: string;
  bank_transaction_id: string;
  kind: DebitExplanationKind;
  funds_reference_number: string | null;
  note: string | null;
  /** `person` picked it; `rail_trace` is the system, from an exact match. */
  proposal_source: 'person' | 'rail_trace';
  proposed_by_name: string | null;
  proposed_at: string;
  /** The reader proposed it: they may take it back and may not confirm it. */
  proposed_by_me: boolean;
  amount: string;
  currency_code: string;
  posted_at: string;
  /** For `other`: `attachment` (a file kept with its hash) or `provider_reference`. */
  evidence_kind: 'attachment' | 'provider_reference' | null;
  attachment_sha256: string | null;
}

export interface ProposePayload {
  kind: DebitExplanationKind;
  /** Required for, and only for, an operator transfer. */
  funds_transfer_id?: string | null;
  note?: string | null;
  /** `other` only: the document that shows what the debit is (PDF, PNG or JPEG, up to 1 MB). */
  attachment?: File | null;
}

const BASE = '/api/v1/ops/bank-debits';

/** Explaining a bank debit nothing else explains (`/api/v1/ops/bank-debits`). */
class BankDebitsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async listUnexplained(limit = 200): Promise<UnexplainedDebit[]> {
    const res = await this.api.get<UnexplainedDebit[]>(`${BASE}/unexplained`, { params: { limit } });
    return res.data;
  }

  async listProposals(limit = 200): Promise<DebitProposal[]> {
    const res = await this.api.get<DebitProposal[]>(`${BASE}/proposals`, { params: { limit } });
    return res.data;
  }

  async transferCandidates(bankTransactionId: string): Promise<TransferCandidate[]> {
    const res = await this.api.get<TransferCandidate[]>(
      `${BASE}/${bankTransactionId}/transfer-candidates`,
    );
    return res.data;
  }

  async propose(bankTransactionId: string, body: ProposePayload): Promise<string> {
    const form = new FormData();
    form.append('kind', body.kind);
    if (body.funds_transfer_id) form.append('funds_transfer_id', body.funds_transfer_id);
    if (body.note) form.append('note', body.note);
    if (body.attachment) form.append('attachment', body.attachment, body.attachment.name);
    const res = await this.api.post<{ explanation_id: string }>(
      `${BASE}/${bankTransactionId}/explanations`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data.explanation_id;
  }

  /** A 15-minute link to the file attached to an explanation (opening it is audited). */
  async attachmentLink(explanationId: string): Promise<string> {
    const res = await this.api.get<{ url: string }>(`${BASE}/explanations/${explanationId}/attachment`);
    return res.data.url;
  }

  /** A manager other than the proposer, with a step-up code. Answers 204. */
  async confirm(explanationId: string, mfaCode: string): Promise<void> {
    await this.api.post(`${BASE}/explanations/${explanationId}/confirm`, { mfa_code: mfaCode });
  }

  /** A manager refuses it, or the proposer takes it back. Answers 204. */
  async decline(explanationId: string, note: string): Promise<void> {
    await this.api.post(`${BASE}/explanations/${explanationId}/decline`, { note });
  }
}

export default new BankDebitsService();
